/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useState } from "react"
import { IRecord } from "../../types/schema"
import { useGlobalState } from "../GlobalStateProvider"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { Fields } from "@cd2/forms"

type IContact = IRecord<"contacts", "name" | "email" | "phone" | "is_default">

export function ContactSelectList({
  value,
  onChange,
  label,
  disabled,
  branchId,
  customerId,
  subCustomerId,
  skipBranchFilter,
  isClearable,
  skeemFilter,
  error,
  showNoRecordsMessage,
}: {
  value?: string
  onChange(id: string, contact?: IContact): void
  label?: string
  disabled?: boolean
  isClearable?: boolean
  branchId?: string
  customerId?: string
  subCustomerId?: string
  skipBranchFilter?: boolean
  skeemFilter?: any
  error?: string
  showNoRecordsMessage?: boolean
}) {
  const skeem = window.skeem
  const { currentUser } = useGlobalState()

  const [records, setRecords] = useState<IContact[]>([])

  function handleChange(id: string) {
    onChange(
      id,
      records.find(v => v.id === id),
    )
  }

  useAsyncEffect(
    async ({ isAlive }) => {
      const records = await skeem.fetch2<IContact[]>({
        model: {
          name: "contacts",
          attributes: ["name", "email", "phone", "is_default"],
          filter: {
            and: [
              !skipBranchFilter && { matches: { branch: branchId ?? currentUser.branch.id } },
              customerId && { matches: { customer: customerId } },
              subCustomerId && {
                gte: [
                  {
                    count: {
                      path: [{ name: "sub_customers", filter: { matches: { id: subCustomerId } } }],
                    },
                  },
                  { value: 1 },
                ],
              },
              skeemFilter,
            ].filter(Boolean),
          },
          sort: { by: "is_default", direction: "desc" },
        },
      })
      if (isAlive()) {
        setRecords(records)
      }
    },
    [branchId, customerId, subCustomerId],
  )

  if (records.length === 0) {
    return (
      <Fields.Select2
        label={label ?? "Contact"}
        noOptionsMessage={
          showNoRecordsMessage ? () => "There are no contacts available" : undefined
        }
        disabled={!showNoRecordsMessage}
      />
    )
  }

  const opts = records?.map(record => ({ value: record.id, label: record.name }))
  return (
    <Fields.Select2
      label={label ?? "Contact"}
      options={records?.map(record => ({ value: record.id, label: record.name }))}
      onChange={opt => handleChange(opt?.value)}
      value={opts.find(record => record.value === value)}
      disabled={disabled}
      isClearable={isClearable}
      error={error}
      components={{
        SingleValue: props => {
          const { innerProps, getStyles } = props
          const record = records.find(record => record.id === props.data?.value)

          return (
            <div
              {...innerProps}
              style={{
                ...getStyles("singleValue", props),
              }}
            >
              <div>{record?.name}</div>
              <div
                css={css`
                  color: #333;
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                {record?.email ?? "No email"}
              </div>
            </div>
          )
        },
        Option: props => {
          const { innerProps, innerRef, getStyles } = props
          const record = records.find(record => record.id === props.data?.value)

          return (
            <div
              ref={innerRef}
              {...innerProps}
              style={{
                ...getStyles("option", props),
              }}
            >
              <div>{record?.name}</div>
              <div
                css={css`
                  color: ${props.isSelected ? "white" : "#333"};
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                {record?.email ?? "No email"}
              </div>
            </div>
          )
        },
      }}
    />
  )
}
